import { computed, watch } from 'vue';
import { defineStore, acceptHMRUpdate, storeToRefs } from 'pinia';
import {
  useInvalidateMaterialSwatches,
  useMaterialSwatchMutations,
  useOrderMaterialSwatchesQuery,
} from '@/queries/materialSwatches';
import { useOrderFeaturesStore } from '@/stores/orderFeatures';
import { useOrderStore } from '@/stores/order';
import { useActiveUserStore } from '@/stores/user';

export const useMaterialSwatchesStore = defineStore('materialSwatches', () => {
  const { orderId, activeVariantId } = storeToRefs(useOrderStore());
  const { isAdmin, isLoggedIn } = storeToRefs(useActiveUserStore());
  const featuresStore = useOrderFeaturesStore();

  const canSee = computed(() => isLoggedIn.value && isAdmin.value);
  const { materialSwatches: cached, query } = useOrderMaterialSwatchesQuery(
    orderId,
    { enabled: canSee }
  );
  const materialSwatches = computed(() => (canSee.value ? cached.value : []));

  const mutations = useMaterialSwatchMutations({
    orderId,
    variantId: activeVariantId,
  });

  const invalidateMaterialSwatches = useInvalidateMaterialSwatches();
  const backfillPending = computed(() => {
    const state = featuresStore.getFeature('material-swatches');
    return Boolean(state?.enabled) && !state?.backfillCompletedAt;
  });
  watch(backfillPending, (isPending, wasPending) => {
    if (!wasPending || isPending) return;
    invalidateMaterialSwatches(orderId.value);
  });

  const isLoading = computed(() => query.isLoading.value);

  return { materialSwatches, isLoading, ...mutations };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useMaterialSwatchesStore, import.meta.hot)
  );
}
